import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * The words of the Numbers & SIP rows (numbers-sip-sync, 2026-10-10) — the prototype's `num.rows`, `num.released`
 * and `NUM_ACTIVITY` (`design/WeTalk.dc.html`, "Numbers and SIP"), written from what the API returns.
 *
 * The prototype's words where the data backs them, and a truthful word where it does not:
 *
 *   · a number is "Working" only while its binding is switched on, "Not in use" while no agent is connected, and
 *     "Turned off" while it is connected but its binding is off (a disabled trunk, a paused workspace) — the
 *     prototype has no row in that state, and "Working" would be a claim;
 *   · "Makes calls" only for a trunk number marked as caller ID on a trunk that can dial (an inbound-only trunk,
 *     plan-015 SIP, cannot); a carrier number's binding answers, and nothing in the row says it dials;
 *   · "Why it was released" is how it left (`release_cause`) — nobody typed a reason, so none is invented.
 */
import { endReasonWords } from "./sip-trunk-copy.js";
import { maskedPartyOf } from "@wetalk/view-model/party-mask";
import type { NumberActivityConversationView, NumberReleaseCause, NumberRowView } from "../../api/gateway.js";

export type NumberStatus = "working" | "turned_off" | "not_in_use" | "setting_up";

export const NUMBER_STATUS_WORD: Readonly<Record<NumberStatus, string>> = {
  get working() { return portalText("finance", "merge.numbers.working"); },
  get turned_off() { return portalText("finance", "merge.numbers.off"); },
  get not_in_use() { return portalText("finance", "merge.numbers.notInUse"); },
  get setting_up() { return portalText("finance", "merge.numbers.settingUp"); },
};

/** The prototype's tag per status: Working in the accent, Setting up outlined, the rest neutral. */
export const NUMBER_STATUS_TONE: Readonly<Record<NumberStatus, "accent" | "outline" | "neutral">> = {
  working: "accent",
  turned_off: "neutral",
  not_in_use: "neutral",
  setting_up: "outline",
};

export function numberStatusOf(row: NumberRowView): NumberStatus {
  if (row.channelBindingId === null) return "not_in_use";
  return row.enabled === true ? "working" : "turned_off";
}

/** What the number does: `{ answers, dials }` — `dials` needs a trunk that can dial. */
export function numberRoleOf(row: NumberRowView, trunkDials: boolean): { readonly answers: boolean; readonly dials: boolean } {
  return { answers: row.channelBindingId !== null, dials: row.sipTrunkId !== null && row.isCallerId && trunkDials };
}

/**
 * `Answers calls`, `Makes calls · Greece`, `Answers and makes calls · Greece`, or `—` (the prototype's `n.conn`). The
 * countries are the trunk's allowed destinations (`allowed_destination_country`) — where it may call — named in English.
 */
export function connectionWordOf(
  role: { readonly answers: boolean; readonly dials: boolean },
  destinationCountry: readonly string[] = [],
): string {
  const where =
    destinationCountry.length === 0
      ? ""
      : ` · ${destinationCountry.map((code) => new Intl.DisplayNames([portalLocale()], { type: "region" }).of(code) ?? code).join(", ")}`;
  if (role.answers && role.dials) return portalText("finance", "merge.numbers.answersMakesWhere", { where });
  if (role.answers) return portalText("finance", "merge.numbers.answersCalls");
  if (role.dials) return portalText("finance", "merge.numbers.makesWhere", { where });
  return "—";
}

/**
 * The prototype's `n.provider`: `Requested from wetalk` (a number wetalk issued) or `Your phone provider` (your own
 * trunk, or a number you brought). AMENDED 2026-10-15 (design audit): a carrier is never named on a customer surface,
 * so a brought number carried by wetalk's carrier reads as the prototype's `Your phone provider`, not the carrier.
 */
export function providerWordOf(row: NumberRowView): string {
  if (row.sipTrunkId === null && row.source === "platform_issued") return portalText("finance", "merge.numbers.requested");
  return portalText("finance", "merge.numbers.yourProvider");
}

/** "Connected through": the provider, and for a trunk number which of your trunks it is on. */
export function connectedThroughOf(row: NumberRowView): string {
  const provider = providerWordOf(row);
  return row.sipTrunkDisplayName === null ? provider : `${provider} · ${row.sipTrunkDisplayName}`;
}

/** The agent half of the row's line — the prototype's `Not used by an agent` when none answers on it. */
export function voiceAgentWordOf(row: NumberRowView): string {
  return row.channelBindingId === null || row.voiceAgentName === null ? portalText("finance", "merge.numbers.notUsed") : row.voiceAgentName;
}

/** `1,284 calls this month`, `1 call this month`, `No calls this month`. */
export function monthWordOf(count: number): string {
  if (count === 0) return portalText("finance", "merge.numbers.noMonthCalls");
  return portalText("finance", "merge.numbers.monthCalls", { count, formatted: new Intl.NumberFormat(portalLocale() === "el" ? "el-GR" : "en-GB").format(count) });
}

export const RELEASE_CAUSE_WORD: Readonly<Record<NumberReleaseCause, string>> = {
  get sip_trunk_deleted() { return portalText("finance", "merge.numbers.deletedTrunk"); },
  get removed_from_sip_trunk() { return portalText("finance", "merge.numbers.removedTrunk"); },
  get released_by_wetalk() { return portalText("finance", "merge.numbers.wetalkReleased"); },
  get released() { return portalText("finance", "merge.numbers.released"); },
};

/**
 * `+39 ··· 0688` — the far end with the middle withheld, as the prototype writes it. Moved to
 * `@wetalk/view-model/party-mask` (plan-034 VIEW-PKG); re-exported here so no portal importer changes.
 */
export { maskedPartyOf };

/** `1:34` — a talk time in minutes and seconds. */
export function clockOf(second: number): string {
  return `${String(Math.floor(second / 60))}:${String(second % 60).padStart(2, "0")}`;
}

export type ActivityMark = "in" | "out" | "miss";

/** One "Recent activity" line: the icon, the title, the detail. */
export function activityLineOf(one: NumberActivityConversationView): {
  readonly mark: ActivityMark;
  readonly title: string;
  readonly detail: string;
} {
  const party = one.partyAddress === null ? null : maskedPartyOf(one.partyAddress);
  const title =
    one.direction === "inbound"
      ? party === null
        ? portalText("finance", "merge.numbers.incomingCall")
        : portalText("finance", "merge.numbers.callFrom", { party })
      : party === null
        ? portalText("finance", "merge.numbers.outgoingCall")
        : portalText("finance", "merge.numbers.called", { party });
  const outcome = one.answered
    ? [portalText("finance", "merge.numbers.answered"), one.billedSecond === null ? null : clockOf(one.billedSecond), numberActivityOutcomeText(one.outcome)]
        .filter((part): part is string => part !== null)
        .join(" · ")
    : endReasonWords(one.endReason);
  const detail = one.isTest ? portalText("finance", "merge.numbers.testOutcome", { outcome }) : outcome;
  const mark: ActivityMark = !one.answered && one.direction === "inbound" ? "miss" : one.direction === "inbound" ? "in" : "out";
  return { mark, title, detail };
}

/** Translate known system outcomes; preserve any customer-authored outcome. */
export function numberActivityOutcomeText(outcome: string | null): string | null {
  return outcome === "Order placed" ? portalText("finance", "merge.numbers.orderPlaced") : outcome;
}
